/**
 * Per-contact AI enrichment action handlers extracted verbatim from
 * `ContactEditModal.tsx` and parameterized on typed deps.
 *
 * The orchestrator keeps the (non-memoized) `handleEnrich`/`handleSaveEnrichment`
 * shells and the auto-enrich `useEffect` shell (deps `[contact?.id]`),
 * delegating the bodies here. Behavior is unchanged (#3293 Pass-2).
 *
 * IMPORTANT: the deps object carries the field VALUES captured at call time
 * (firstName, phone, company, …), not live getters — this preserves the
 * original closure semantics where post-await `if (!phone.trim())` blank-fill
 * guards read the value snapshotted when the handler fired.
 */

import { emailApiBase } from '@/lib/email-api';
import type { CRMColumnConfig } from '@/types/modules';
import type { ContactObject } from './types';

export interface EnrichDeps {
  firstName: string;
  lastName: string;
  email: string;
  contact: ContactObject | null | undefined;
  company: string;
  firm: string;
  location: string;
  columns: CRMColumnConfig[];
  stage: string;
  phone: string;
  linkedinUrl: string;
  summary: string;
  contactTitle: string;
  setEnrichLoading: (b: boolean) => void;
  setEnrichError: (s: string | null) => void;
  setEnrichResult: (r: { confidence: string; fields: Record<string, any>; rawSummary: string } | null) => void;
  setEnrichHintsPrompt: (b: boolean) => void;
  setPhone: (s: string) => void;
  setCompany: (s: string) => void;
  setFirm: (s: string) => void;
  setContactTitle: (s: string) => void;
  setEnrichmentSummary: (s: string) => void;
  setEnrichmentSkills: (s: string[]) => void;
  setEnrichmentConfidence: (s: string) => void;
  setEnrichmentAttemptedAt: (s: string) => void;
  setLocation: (s: string) => void;
  setLinkedinUrl: (s: string) => void;
  setXHandle: (s: string) => void;
  setSummary: (s: string) => void;
  setEnrichUnsaved: (b: boolean) => void;
}

export async function handleEnrich(extraHintsArg: string | unknown | undefined, deps: EnrichDeps): Promise<void> {
  const {
    firstName, lastName, email, contact, company, firm, location, columns, stage, phone,
    linkedinUrl, summary, contactTitle, setEnrichLoading, setEnrichError, setEnrichResult,
    setEnrichHintsPrompt, setPhone, setCompany, setFirm, setContactTitle, setEnrichmentSummary,
    setEnrichmentSkills, setEnrichmentConfidence, setEnrichmentAttemptedAt, setLocation,
    setLinkedinUrl, setXHandle, setSummary, setEnrichUnsaved,
  } = deps;

  // Defend against MouseEvent being passed as arg from onClick handlers
  const extraHints = typeof extraHintsArg === 'string' ? extraHintsArg : undefined;
  const searchName = `${firstName.trim()} ${lastName.trim()}`.trim();
  if (!searchName) return;

  setEnrichLoading(true);
  setEnrichError(null);
  setEnrichResult(null);
  setEnrichHintsPrompt(false);

  // Build hints from company + any extra hints the user provided.
  // Also fall back to a domain-derived company when no explicit company
  // is set — significantly improves disambiguation for low-presence pros.
  const emailValue = email.trim() || (Array.isArray((contact as any)?.emails) ? (contact as any).emails[0] : '');
  const genericDomains = new Set(['gmail.com','yahoo.com','hotmail.com','outlook.com','aol.com','icloud.com','me.com','mail.com','protonmail.com','proton.me','live.com','msn.com','ymail.com','comcast.net','att.net','verizon.net','sbcglobal.net','cox.net','charter.net']);
  const inferredCompanyFromDomain = (() => {
    const domain = emailValue?.split('@')[1]?.toLowerCase();
    if (!domain || genericDomains.has(domain)) return '';
    const raw = domain.split('.')[0].replace(/[-_]/g, ' ');
    return raw.replace(/\b\w/g, (c: string) => c.toUpperCase());
  })();
  const effectiveCompany = company.trim() || firm.trim() || inferredCompanyFromDomain;
  // Custom kanban column labels carry meaningful disambiguation
  // (e.g. "Bitcoin", "Mentors") — fold them into hints. The three
  // built-in tier labels (Inner Circle / Active / Network) are
  // generic relationship buckets and would mislead web search, so
  // strip them.
  const GENERIC_COLUMN_LABELS = new Set(['inner circle', 'active', 'network', 'lead', 'leads']);
  const currentColumn = columns.find(col => col.stage === stage);
  const columnHint = currentColumn?.label && !GENERIC_COLUMN_LABELS.has(currentColumn.label.toLowerCase().trim())
    ? currentColumn.label.trim()
    : '';
  const hintParts = [effectiveCompany, location.trim(), columnHint, extraHints?.trim()].filter(Boolean);
  const hints = hintParts.join(', ') || undefined;
  const knownData: Record<string, string> = {};
  if (emailValue) knownData.email = emailValue;
  if (effectiveCompany) knownData.company = effectiveCompany;
  if (phone.trim()) knownData.phone = phone.trim();
  if (linkedinUrl.trim()) knownData.linkedinUrl = linkedinUrl.trim();
  if (location.trim()) knownData.location = location.trim();

  try {
    const { apiPost } = await import('@/lib/api/client');
    // Pre-fetch a recent email by this contact and pull phone/title/
    // company from their signature — gives the LLM ground truth and
    // also captures the phone even if the LLM can't find one.
    let sig: { phone?: string; company?: string; title?: string } = {};
    if (emailValue) {
      try {
        const { data: searchData } = await apiPost<{ threads?: Array<{ id: string }> }>(
          emailApiBase() + '/search',
          { query: `from:${emailValue}`, max: 1 },
        );
        const threadId = searchData.threads?.[0]?.id;
        if (threadId) {
          const { data: threadData } = await apiPost<{ messages?: Array<{ from?: string; body?: string }> }>(
            emailApiBase() + '/thread',
            { threadId },
          );
          const fromMsg = (threadData.messages || []).find(m =>
            (m.from || '').toLowerCase().includes(emailValue.toLowerCase()),
          );
          if (fromMsg?.body) {
            const { data: parsed } = await apiPost<Record<string, any>>(
              '/contacts/parse',
              { text: fromMsg.body },
            );
            if (parsed) sig = { phone: parsed.phone, company: parsed.company, title: parsed.title };
          }
        }
      } catch { /* signature extraction is best-effort */ }
    }
    // Apply signature data immediately to empty form fields, even
    // before web enrichment returns. Phone in particular is ground
    // truth from the contact's own email — never overwrite an
    // existing value, just fill blanks.
    if (sig.phone && !phone.trim()) setPhone(sig.phone);
    if (sig.company && !company.trim() && !firm.trim()) {
      setCompany(sig.company);
      setFirm(sig.company);
    }
    if (sig.title && !contactTitle.trim()) setContactTitle(sig.title);
    // Fold sig data into knownData so the LLM treats it as truth.
    const mergedKnown = { ...knownData };
    if (sig.phone && !mergedKnown.phone) mergedKnown.phone = sig.phone;
    if (sig.company && !mergedKnown.company) mergedKnown.company = sig.company;
    if (sig.title && !mergedKnown.title) mergedKnown.title = sig.title;

    const { data } = await apiPost<{
      confidence: string;
      fields: Record<string, any>;
      rawSummary: string;
    }>('/contacts/enrich', {
      name: searchName,
      hints: hints || (sig.company ? sig.company : undefined),
      knownData: Object.keys(mergedKnown).length ? mergedKnown : undefined,
    });

    setEnrichResult(data);

    // Only show hints prompt automatically if NO results at all
    if (data.confidence === 'none' && !extraHints) {
      setEnrichHintsPrompt(true);
    }

    // Persist enrichment data. On 'none', `rawSummary` is the API's
    // help-text ("No results found for X. Try adding keywords...") and
    // must NOT be saved as the contact's profile body — bulk path skips
    // it for the same reason; the per-contact path now matches.
    if (data.rawSummary && data.confidence !== 'none') setEnrichmentSummary(data.rawSummary);
    if (data.fields?.skills?.length) setEnrichmentSkills(data.fields.skills);
    if (data.confidence) setEnrichmentConfidence(data.confidence);
    // Stamp every attempt — even low/none — so bulk enhance and the
    // banner know not to re-burn tokens on this contact.
    setEnrichmentAttemptedAt(new Date().toISOString());

    // Auto-fill empty fields
    if (data.fields) {
      if (!company.trim() && data.fields.company) { setCompany(data.fields.company); }
      if (!contactTitle.trim() && data.fields.contactTitle) { setContactTitle(data.fields.contactTitle); }
      if (!location.trim() && data.fields.location) { setLocation(data.fields.location); }
      if (!linkedinUrl.trim() && data.fields.linkedinUrl) { setLinkedinUrl(data.fields.linkedinUrl); }
      if (data.fields.twitterHandle) { setXHandle(data.fields.twitterHandle.replace(/^@/, '')); }
      if (!phone.trim() && data.fields.phone) { setPhone(data.fields.phone); }
      if (!summary.trim() && data.fields.bio) { setSummary(data.fields.bio); }
      if (!firm.trim() && data.fields.company) { setFirm(data.fields.company); }
    }

    // Mark enrichment as unsaved so the Save Profile button appears
    setEnrichUnsaved(true);
  } catch (err: any) {
    setEnrichError(err.message || 'Enrichment failed');
  } finally {
    setEnrichLoading(false);
  }
}

export interface SaveEnrichmentDeps {
  contact: ContactObject | null | undefined;
  isNew: boolean | undefined;
  enrichmentSummary: string;
  enrichmentSkills: string[];
  enrichmentConfidence: string;
  enrichmentAttemptedAt: string;
  contactTitle: string;
  linkedinUrl: string;
  location: string;
  firm: string;
  company: string;
  summary: string;
  xHandle: string;
  phone: string;
  enrichHints: string;
  onSave: (data: Partial<ContactObject>) => void;
  setEnrichUnsaved: (b: boolean) => void;
}

export function handleSaveEnrichment(deps: SaveEnrichmentDeps): void {
  const {
    contact, isNew, enrichmentSummary, enrichmentSkills, enrichmentConfidence, enrichmentAttemptedAt,
    contactTitle, linkedinUrl, location, firm, company, summary, xHandle, phone, enrichHints,
    onSave, setEnrichUnsaved,
  } = deps;
  if (!contact || isNew) return;
  const saveData: Record<string, any> = {};
  if (enrichmentSummary) saveData.enrichmentSummary = enrichmentSummary;
  if (enrichmentSkills.length) saveData.enrichmentSkills = enrichmentSkills;
  if (enrichmentConfidence) saveData.enrichmentConfidence = enrichmentConfidence;
  if (enrichmentAttemptedAt) saveData.enrichmentAttemptedAt = enrichmentAttemptedAt;
  if (contactTitle) saveData.contactTitle = contactTitle;
  if (linkedinUrl) saveData.linkedinUrl = linkedinUrl;
  if (location) saveData.location = location;
  if (firm) saveData.firm = firm;
  if (company) saveData.organization = company;
  if (summary) saveData.summary = summary;
  if (xHandle) saveData.xHandle = xHandle;
  if (phone) saveData.phone = phone;
  if (enrichHints.trim()) saveData.enrichHints = enrichHints.trim();
  onSave(saveData as any);
  setEnrichUnsaved(false);
}
